// src/services/logService.js
import api from './api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const processLogForExport = (log) => {
  return {
    ...log,
    createdAt: log.createdAt ? 
      format(new Date(log.createdAt), "dd/MM/yyyy HH:mm:ss", { locale: ptBR }) : 
      '',
    metadados: log.metadados ? 
      JSON.stringify(log.metadados, null, 2).replace(/\\"/g, '"') : 
      ''
  };
};

const prepareQueryParams = (filtros = {}) => {
  const params = new URLSearchParams();
  
  if (filtros.instituicaoId) {
    params.append('instituicaoId', filtros.instituicaoId);
  }
  
  if (filtros.acao && filtros.acao !== '') {
    params.append('acao', filtros.acao);
  }
  
  if (filtros.entidade && filtros.entidade !== '') {
    params.append('entidade', filtros.entidade);
  }
  
  if (filtros.status && filtros.status !== '') {
    params.append('status', filtros.status);
  }
  
  if (filtros.dataInicio) {
    const dataFormatada = format(new Date(filtros.dataInicio), "yyyy-MM-dd");
    params.append('dataInicio', dataFormatada);
  }
  
  if (filtros.dataFim) {
    const dataFormatada = format(new Date(filtros.dataFim), "yyyy-MM-dd");
    params.append('dataFim', dataFormatada);
  }

  return params;
};

// Restaurando as funções originais de logs forense
export const listLogsForense = async () => {
  console.log('Buscando logs forense...');
  try {
    const response = await api.get('/logs/forense');
    console.log('Resposta logs forense:', response.data);
    return response;
  } catch (error) {
    console.error('Erro ao buscar logs forense:', error);
    throw error;
  }
};

export const getForensicLogs = async () => {
  try {
    const response = await api.get('/logs/forense');
    return response;
  } catch (error) {
    console.error('Erro ao buscar logs forense:', error);
    throw error;
  }
};

export const logService = {
  async listLogs(filtros = {}) {
    const params = prepareQueryParams(filtros);
    try {
      const response = await api.get(`/logs?${params.toString()}`);
      return response;
    } catch (error) {
      console.error('Erro ao listar logs:', error);
      throw error;
    }
  },

  async listLogsInstituicao(instituicaoId, filtros = {}) {
    if (!instituicaoId) {
      throw new Error('ID da instituição é obrigatório');
    }
    const params = prepareQueryParams({ ...filtros, instituicaoId });
    try {
      const response = await api.get(`/logs?${params.toString()}`);
      return response;
    } catch (error) {
      console.error('Erro ao listar logs da instituição:', error);
      throw error;
    }
  },

  // Mantendo as funções originais dentro do objeto de serviço também
  listLogsForense,
  getForensicLogs,

  async getAccessLogs(filtros = {}) {
    const params = prepareQueryParams(filtros);
    try {
      const response = await api.get(`/logs/acesso?${params.toString()}`);
      return response;
    } catch (error) {
      console.error('Erro ao listar logs de acesso:', error);
      throw error;
    }
  },

  async getLogDetails(logId) {
    if (!logId) {
      throw new Error('ID do log é obrigatório');
    }
    try {
      const response = await api.get(`/logs/${logId}`);
      return response;
    } catch (error) {
      console.error('Erro ao obter detalhes do log:', error);
      throw error;
    }
  },

  async exportLogs(filtros = {}) {
    try {
      const params = prepareQueryParams(filtros);
      const response = await api.get(`/logs?${params.toString()}`);

      if (!response.data) {
        throw new Error('Nenhum dado recebido do servidor');
      }

      const logs = Array.isArray(response.data) ? response.data : 
                   (response.data.data ? response.data.data : []);

      const processedLogs = logs.map(processLogForExport);

      const headers = [
        'Data',
        'Usuário',
        'Ação',
        'Tipo Log',
        'Nível Sensibilidade',
        'Entidade',
        'ID Entidade',
        'Descrição',
        'Instituição',
        'IP',
        'Método',
        'Rota',
        'Status'
      ].join(',');

      const csvContent = processedLogs.map(log => [
        log.createdAt,
        log.usuario?.nome || '',
        log.acao || '',
        log.tipoLog || '',
        log.nivelSensibilidade || '',
        log.entidade || '',
        log.entidadeId || '',
        (log.descricao || '').replace(/,/g, ';'),
        log.instituicao?.nome || '',
        log.ip || '',
        log.metodo || '',
        log.rota || '',
        log.status || ''
      ].map(value => `"${value}"`).join(','));

      const csvData = [headers, ...csvContent].join('\n');
      const blob = new Blob(['\ufeff' + csvData], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = format(new Date(), "dd-MM-yyyy_HH-mm", { locale: ptBR });
      
      link.href = url;
      link.setAttribute('download', `logs_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('Erro ao exportar logs:', error);
      throw error;
    }
  },

  async exportLogsInstituicao(instituicaoId, filtros = {}) {
    if (!instituicaoId) {
      throw new Error('ID da instituição é obrigatório para exportação');
    }
    return this.exportLogs({ ...filtros, instituicaoId });
  }
};

export const buscarPerfilAtual = async () => {
  try {
    const response = await api.get('/perfis-usuario/atual');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar perfil atual:', error.response?.data);
    return null; // Retorna null se não encontrar perfil
  }
};



export const {
  listLogs,
  listLogsInstituicao,
  getAccessLogs,
  getLogDetails,
  exportLogs,
  exportLogsInstituicao
} = logService;

export default logService;